import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router';
import useCsvPlugin from '@usecsv/js';
import { observer } from 'mobx-react-lite';
import { ElevatedButton } from '@producer/ui-kit';

import { createBatchContacts } from '../../features/contacts/stores/contact.slice';
import { PagePlaceholder } from '../../components/page-placeholder/page-placeholder';
import { globalConfig } from '../../configuration/config';
import { ContactSearchBar } from '../../features/contacts/components/contact-search.bar';
import { searchContacts } from '../../features/contacts/components/utils/search-contacts';
import { CreateContactDrawer } from '../../features/contacts/components/create-contact.drawer';
import { ContactList } from '../../features/contacts/components/contact-list';
import { ContactDetailsPage } from './contact-detail.page';

import type { Contact } from '../../app/entities';
import { Header } from '../../app/components/header';

import styles from './contacts-list.screen.module.scss';
type ContactListPageProps = {
  contacts: Contact[];
};

const transform = (rows: any[]) =>
  rows.map((row) => ({
    firstName: row['First Name'],
    lastName: row['Last Name'],
    role: row['Role'],
    email: row['Email'],
    phone: row['Phone'],
    city: row['City'],
    country: row['Country'],
  }));

export const ContactsListScreen: React.FC<ContactListPageProps> = observer(({ contacts }) => {
  const navigate = useNavigate();
  const { spaceId } = useParams<{ spaceId: string }>();
  const [searchValue, setSearchValue] = useState('');
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>(contacts);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleContactImport = async (data: any) => {
    await createBatchContacts({
      spaceId: spaceId!,
      contacts: transform(data.rows),
    });
  };

  const hasContacts = !!contacts?.length;
  const importerKey = globalConfig.config.csvImporterKey;

  useEffect(() => {
    setFilteredContacts(searchContacts(contacts, searchValue));
  }, [searchValue, contacts]);

  const selectCreatedContact = (contact: Contact) => {
    navigate(`../${contact._id}`);
  };

  const openImportModal = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCsvPlugin({
      importerKey,
      onData: handleContactImport,
    });
  };

  return (
    <div className={styles['page']}>
      <Header title="Contacts">
        <ContactSearchBar onChange={setSearchValue} key="search-contacts" value={searchValue} />

        {importerKey && <ElevatedButton text="Import" onClick={openImportModal} />}

        <ElevatedButton
          key="create-contact"
          onClick={() => setIsCreateModalOpen(true)}
          text="Add contact"
        />
      </Header>

      <div className={styles['container']}>
        {hasContacts ? (
          <>
            <div className={styles['scroll-container']}>
              <ContactList contacts={filteredContacts} />
            </div>

            <Routes>
              <Route path="/*" element={<ContactDetailsPage />} />
            </Routes>
          </>
        ) : (
          <PagePlaceholder
            description='Contacts will be displayed here, you can add them by clicking on "Import" or "Create
        Contact"'
            actions={[
              <React.Fragment key="import-contacts">
                {importerKey && <ElevatedButton text="Import" onClick={openImportModal} />}
              </React.Fragment>,
              <ElevatedButton
                key="create-contact"
                onClick={() => setIsCreateModalOpen(true)}
                text="Create contact"
              />,
            ]}
          />
        )}

        {isCreateModalOpen && (
          <CreateContactDrawer
            spaceId={spaceId!}
            onCancel={() => setIsCreateModalOpen(false)}
            onSubmit={selectCreatedContact}
          />
        )}
      </div>
    </div>
  );
});
